import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Check, Trash2, Plus, Trophy, UserPlus, CalendarDays, MapPin, Users, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { formatDate } from "./constants";
import Avatar from "@/components/Avatar";

export default function GalaView({ match, isCoach, onReload }) {
  return (
    <div className="space-y-6" data-testid="gala-view">
      <GalaHeader match={match} isCoach={isCoach} onReload={onReload} />
      <GalaGuestsPanel match={match} isCoach={isCoach} onReload={onReload} />
      <GalaScoreSheet match={match} isCoach={isCoach} onReload={onReload} />
    </div>
  );
}

function GalaHeader({ match, isCoach, onReload }) {
  const isFinished = match.status === "finished";
  const present = (match.availabilities || []).filter((a) => a.status === "available");
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900 to-[#0B0F17] p-6 sm:p-8">
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 no-print" />
      <div className="relative">
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-purple-300 mb-3 flex items-center gap-2">
          <Sparkles className="w-3 h-3" /> {isFinished ? "Gala · Terminé" : "Gala · À venir"}
        </div>
        <h1
          data-testid="match-title"
          className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight"
        >
          {match.title || "Match de gala"}
        </h1>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <EditableTeamName
            match={match}
            field="team_a_name"
            defaultLabel="Équipe A"
            color="#A855F7"
            isCoach={isCoach}
            onReload={onReload}
            testidPrefix="gala-team-a"
          />
          <EditableTeamName
            match={match}
            field="team_b_name"
            defaultLabel="Équipe B"
            color="#D946EF"
            isCoach={isCoach}
            onReload={onReload}
            testidPrefix="gala-team-b"
          />
        </div>
        {isFinished && (
          <div
            className="mt-6 flex items-center gap-4 flex-wrap font-display font-black text-4xl sm:text-5xl"
            data-testid="final-score-gala"
          >
            <span className="text-purple-300">{match.team_a_name || "Équipe A"} {match.score_team_a ?? 0}</span>
            <span className="text-slate-500">—</span>
            <span className="text-fuchsia-300">{match.score_team_b ?? 0} {match.team_b_name || "Équipe B"}</span>
          </div>
        )}
        <div className="flex flex-wrap gap-4 mt-6 text-sm text-slate-300">
          <span className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4" /> {formatDate(match.match_date)}
          </span>
          <span className="flex items-center gap-2">
            <MapPin className="w-4 h-4" /> {match.location}
          </span>
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4" /> {present.length} présent(s)
          </span>
        </div>
        {match.notes && (
          <div className="mt-4 p-3 rounded-lg bg-slate-800/60 border border-white/5 text-sm text-slate-300">
            <span className="text-[10px] uppercase tracking-widest font-mono-stat text-purple-300 block mb-1">
              Notes coach
            </span>
            {match.notes}
          </div>
        )}
      </div>
    </div>
  );
}

function EditableTeamName({ match, field, defaultLabel, color, isCoach, onReload, testidPrefix }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(match[field] || defaultLabel);
  useEffect(() => {
    setValue(match[field] || defaultLabel);
  }, [match, field, defaultLabel]);

  const save = async () => {
    try {
      const body = {};
      body[field] = value.trim() || defaultLabel;
      await api.patch(`/matches/${match.id}/gala/teams`, body);
      toast.success("Nom d'équipe mis à jour");
      setEditing(false);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div
      className="p-4 rounded-xl border border-white/10 bg-slate-900/60"
      style={{ borderTopColor: color, borderTopWidth: 3 }}
      data-testid={testidPrefix}
    >
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 mb-1">
        {field === "team_a_name" ? "Équipe A" : "Équipe B"}
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            data-testid={`${testidPrefix}-input`}
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()}
            className="flex-1 min-w-0 px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
            maxLength={60}
          />
          <button
            data-testid={`${testidPrefix}-save`}
            onClick={save}
            className="p-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white"
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <div className="font-display font-black text-2xl sm:text-3xl truncate" style={{ color }}>
            {match[field] || defaultLabel}
          </div>
          {isCoach && (
            <button
              data-testid={`${testidPrefix}-edit`}
              onClick={() => setEditing(true)}
              className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"
              title="Renommer"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function GalaGuestsPanel({ match, isCoach, onReload }) {
  const guests = Array.isArray(match.guests) ? match.guests : [];
  const players = match.players || [];
  const rosters = match.rosters || [];

  // Roster mapping for club members: team_a/team_b sides
  const rosterMap = {};
  rosters.forEach((r) => (rosterMap[r.player_id] = r.side));

  const present = (match.availabilities || []).filter((a) => a.status === "available");
  const presentPlayers = present.map((a) => players.find((p) => p.id === a.player_id)).filter(Boolean);
  const inTeamA = presentPlayers.filter((p) => rosterMap[p.id] === "team_a");
  const inTeamB = presentPlayers.filter((p) => rosterMap[p.id] === "team_b");
  const unassigned = presentPlayers.filter((p) => !rosterMap[p.id]);

  const guestsA = guests.filter((g) => g.side === "team_a");
  const guestsB = guests.filter((g) => g.side === "team_b");

  const setSide = async (playerId, side) => {
    // side: "team_a" | "team_b" | null
    try {
      // Compute new roster
      const next = {};
      Object.entries(rosterMap).forEach(([pid, s]) => (next[pid] = s));
      if (side === null) delete next[playerId];
      else next[playerId] = side;
      const assignments = Object.entries(next)
        .filter(([, s]) => s === "team_a" || s === "team_b")
        .map(([pid, s]) => ({ player_id: pid, side: s }));
      await api.post(`/matches/${match.id}/roster`, { assignments });
      onReload && onReload();
    } catch (e) {
      toast.error("Erreur d'assignation");
    }
  };

  const [newGuest, setNewGuest] = useState({ name: "", side: "team_a" });
  const saveGuests = async (nextList) => {
    try {
      await api.post(`/matches/${match.id}/gala/guests`, { guests: nextList });
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const addGuest = () => {
    const name = newGuest.name.trim();
    if (!name) return;
    saveGuests([...guests, { name, side: newGuest.side }]);
    setNewGuest({ name: "", side: newGuest.side });
  };
  const removeGuest = (idx) => {
    saveGuests(guests.filter((_, i) => i !== idx));
  };

  return (
    <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-purple-300">
            Composition
          </div>
          <div className="font-display font-bold text-lg">Équipes du gala</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
        <TeamColumn
          title={match.team_a_name || "Équipe A"}
          color="#A855F7"
          side="team_a"
          members={inTeamA}
          guests={guestsA}
          isCoach={isCoach}
          onSetSide={setSide}
          onRemoveGuest={(g) => removeGuest(guests.findIndex((x) => x.name === g.name && x.side === "team_a"))}
        />
        <TeamColumn
          title={match.team_b_name || "Équipe B"}
          color="#D946EF"
          side="team_b"
          members={inTeamB}
          guests={guestsB}
          isCoach={isCoach}
          onSetSide={setSide}
          onRemoveGuest={(g) => removeGuest(guests.findIndex((x) => x.name === g.name && x.side === "team_b"))}
        />
      </div>

      {isCoach && unassigned.length > 0 && (
        <div className="p-4 border-t border-white/10">
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
            Membres présents non assignés · {unassigned.length}
          </div>
          <div className="flex flex-wrap gap-2">
            {unassigned.map((p) => (
              <div
                key={p.id}
                data-testid={`gala-unassigned-${p.id}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 border border-white/10"
              >
                <Avatar player={p} size={24} />
                <span className="text-sm truncate max-w-[140px]">{p.name}</span>
                <button
                  onClick={() => setSide(p.id, "team_a")}
                  className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
                  data-testid={`gala-assign-a-${p.id}`}
                >
                  → A
                </button>
                <button
                  onClick={() => setSide(p.id, "team_b")}
                  className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-fuchsia-500/20 text-fuchsia-300 hover:bg-fuchsia-500/30"
                  data-testid={`gala-assign-b-${p.id}`}
                >
                  → B
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isCoach && (
        <div className="p-4 border-t border-white/10 space-y-2">
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 flex items-center gap-1">
            <UserPlus className="w-3 h-3" /> Ajouter un invité externe
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              data-testid="gala-guest-name"
              type="text"
              placeholder="Nom du joueur invité"
              value={newGuest.name}
              onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && addGuest()}
              className="flex-1 min-w-[180px] px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
              maxLength={80}
            />
            <select
              data-testid="gala-guest-side"
              value={newGuest.side}
              onChange={(e) => setNewGuest({ ...newGuest, side: e.target.value })}
              className="px-3 py-2 bg-slate-800 border border-white/10 rounded-lg text-white text-sm"
            >
              <option value="team_a">→ {match.team_a_name || "Équipe A"}</option>
              <option value="team_b">→ {match.team_b_name || "Équipe B"}</option>
            </select>
            <button
              data-testid="gala-guest-add"
              onClick={addGuest}
              disabled={!newGuest.name.trim()}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white text-xs font-bold uppercase"
            >
              <Plus className="w-3.5 h-3.5" /> Ajouter
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TeamColumn({ title, color, side, members, guests, isCoach, onSetSide, onRemoveGuest }) {
  return (
    <div className="p-4" data-testid={`gala-col-${side}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-black text-lg" style={{ color }}>
          {title}
        </div>
        <div className="text-[10px] font-mono-stat text-slate-500">
          {members.length + guests.length} joueur(s)
        </div>
      </div>
      <div className="space-y-1.5">
        {members.map((p) => (
          <div
            key={p.id}
            data-testid={`gala-member-${p.id}`}
            className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/50"
          >
            <Avatar player={p} size={28} />
            <div className="flex-1 min-w-0 truncate text-sm">{p.name}</div>
            {isCoach && (
              <button
                onClick={() => onSetSide(p.id, null)}
                className="p-1.5 rounded bg-slate-700/50 hover:bg-rose-500/20 text-slate-400 hover:text-rose-300"
                title="Retirer"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {guests.map((g, i) => (
          <div
            key={`${g.name}-${i}`}
            data-testid={`gala-guest-${side}-${i}`}
            className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/30 border border-dashed border-white/10"
          >
            <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-300">
              INV
            </div>
            <div className="flex-1 min-w-0 truncate text-sm italic text-slate-300">{g.name}</div>
            {isCoach && (
              <button
                onClick={() => onRemoveGuest(g)}
                className="p-1.5 rounded bg-slate-700/50 hover:bg-rose-500/20 text-slate-400 hover:text-rose-300"
                title="Retirer l'invité"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {members.length + guests.length === 0 && (
          <div className="text-xs text-slate-500 italic p-2">Aucun joueur pour le moment</div>
        )}
      </div>
    </div>
  );
}

function GalaScoreSheet({ match, isCoach, onReload }) {
  const [scoreA, setScoreA] = useState(match.score_team_a ?? 0);
  const [scoreB, setScoreB] = useState(match.score_team_b ?? 0);
  const [summary, setSummary] = useState(match.summary || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setScoreA(match.score_team_a ?? 0);
    setScoreB(match.score_team_b ?? 0);
    setSummary(match.summary || "");
  }, [match]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/matches/${match.id}/sheet`, {
        score_team_a: Number(scoreA) || 0,
        score_team_b: Number(scoreB) || 0,
        summary,
        stats: [],
      });
      toast.success("Résultat enregistré");
      onReload && onReload();
    } catch (e) {
      toast.error("Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-slate-900 border border-white/10 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-purple-300" />
        <div className="font-display font-bold text-lg uppercase">Score final</div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <ScoreInput
          label={match.team_a_name || "Équipe A"}
          color="#A855F7"
          value={scoreA}
          onChange={setScoreA}
          disabled={!isCoach}
          testid="gala-score-a"
        />
        <ScoreInput
          label={match.team_b_name || "Équipe B"}
          color="#D946EF"
          value={scoreB}
          onChange={setScoreB}
          disabled={!isCoach}
          testid="gala-score-b"
        />
      </div>

      <div>
        <label className="block text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 mb-2">
          Résumé (optionnel)
        </label>
        <textarea
          data-testid="gala-summary"
          disabled={!isCoach}
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={3}
          className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-lg text-white text-sm disabled:opacity-70"
          placeholder="Notes, meilleurs moments, ambiance..."
        />
      </div>

      {isCoach && (
        <button
          data-testid="gala-submit"
          onClick={submit}
          disabled={saving}
          className="w-full py-3 bg-purple-500 hover:bg-purple-600 text-white font-bold uppercase tracking-wide rounded-lg disabled:opacity-50"
        >
          {match.status === "finished" ? "Mettre à jour le résultat" : "Publier le résultat"}
        </button>
      )}
    </div>
  );
}

function ScoreInput({ label, color, value, onChange, disabled, testid }) {
  return (
    <div
      className="p-4 rounded-xl border border-white/10 bg-slate-800/60"
      style={{ borderTopColor: color, borderTopWidth: 3 }}
    >
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400 truncate">
        {label}
      </div>
      <input
        data-testid={testid}
        type="number"
        min={0}
        max={99}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent text-4xl sm:text-5xl font-display font-black focus:outline-none disabled:opacity-70"
        style={{ color }}
      />
    </div>
  );
}
